import SectionStatusType from '../types/SectionStatusType';

export const X_DEFAULT = 'x-default';
class RouteService {
  constructor(categoryStore, configStore, languageStore, sectionStore) {
    this.categoryStore = categoryStore;
    this.configStore = configStore;
    this.languageStore = languageStore;
    this.sectionStore = sectionStore;
  }

  findCanonicalSectionForCategory(category) {
    if (category) {
      // Return the first public section that includes this category
      return this.sectionStore.sections.find(
        (section) =>
          section.includesCategory(category) &&
          section.status === SectionStatusType.ACTIVE
      );
    }
    return null;
  }

  findCanonicalSection(sectionIds) {
    // Get the first section ID that has a matching section
    // In a multi-domain store there might be section IDs that do not match.
    const defaultSectionId = sectionIds.find((sectionId) =>
      this.sectionStore.findSectionById(sectionId)
    );
    return (
      defaultSectionId && this.sectionStore.findSectionById(defaultSectionId)
    );
  }

  transformBreadCrumbs(breadcrumbs) {
    let transformedBreadcrumbs = [];

    if (this.sectionStore.activeSection) {
      transformedBreadcrumbs.push({
        text: this.sectionStore.activeSection.display_name,
        url: this.getSectionPath(
          this.sectionStore.activeSection,
          this.languageStore.activeLocale
        ),
      });
    }

    return transformedBreadcrumbs.concat(
      breadcrumbs.map((breadcrumb) => {
        const url =
          breadcrumb.url &&
          this.getPath(breadcrumb.url, this.sectionStore.activeSection);
        return {
          ...breadcrumb,
          url,
        };
      })
    );
  }

  getCategoryPath(category, path) {
    let section;
    if (
      this.sectionStore.activeSection &&
      this.sectionStore.activeSection.includesCategory(category)
    ) {
      section = this.sectionStore.activeSection;
    } else {
      section = this.findCanonicalSectionForCategory(category);
    }
    return this.getPath(path || category.path, section);
  }

  getCanonicalCategoryPath(category, path, locale) {
    let section = this.findCanonicalSectionForCategory(category);

    return this.getPath(path || category.path, section, locale);
  }

  getCanonicalProductPath(product, path, locale) {
    // Prioritize main_section_id as canonical section
    let canonicalSection = this.configStore.activateSections
      ? this.findCanonicalSection([product.main_section_id])
      : null;
    const hasCategory = !!product.canonicalCategoryId;

    if (!canonicalSection && hasCategory) {
      const category = this.categoryStore.findCategoryById(
        product.canonicalCategoryId
      );
      canonicalSection = this.findCanonicalSectionForCategory(category);
    }

    return this.getPath(path || product.path, canonicalSection, locale);
  }

  getProductPath(product, path) {
    let section;
    if (
      this.sectionStore.activeSection &&
      product.belongsToSection(this.sectionStore.activeSection.id)
    ) {
      section = this.sectionStore.activeSection;
    } else {
      section = this.findCanonicalSection(product.section_ids);
    }
    return this.getPath(path || product.path, section);
  }

  /**
   * Get a localized section path using the section's alternate_links
   * @param {Section} section - The section object
   * @param {String} locale - The locale (e.g., 'fi-FI')
   * @param {String} domain - Optional domain for multi-domain support
   * @returns {String} - The full localized path
   */
  getSectionPath(section, locale = null, domain = null) {
    if (!section) {
      return '';
    }

    if (locale && section.alternate_links) {
      const localizedPath = section.getLocalizedPath(locale, domain);
      // For sections, getLocalizedPath returns the full path, we just need language prefix
      const { languages } = this.languageStore;
      const currentLocale =
        locale === X_DEFAULT ? this.configStore.defaultLocale : locale;
      const routeLocale = currentLocale
        ? currentLocale.split('-')[0]
        : this.languageStore.activeLocale;
      const languagePrefix = languages.length > 1 ? `/${routeLocale}` : '';
      return languagePrefix + localizedPath;
    }

    // Fall back to regular section path with language prefix
    const { languages } = this.languageStore;
    const currentLocale =
      locale === X_DEFAULT ? this.configStore.defaultLocale : locale;
    const routeLocale = currentLocale
      ? currentLocale.split('-')[0]
      : this.languageStore.activeLocale;
    const languagePrefix = languages.length > 1 ? `/${routeLocale}` : '';
    return languagePrefix + section.path;
  }

  /**
   *
   * @param {String} path
   * @param {Section} section Used when forming whole path with section and sub-categories
   * @param {String} locale
   * @returns {String}
   */
  getPath(path, section, locale) {
    const { languages } = this.languageStore;
    const { defaultLocale, activateSections } = this.configStore;
    const currentLocale = locale === X_DEFAULT ? defaultLocale : locale;

    // Extract language code from hreflang if needed (e.g., "fi-FI" -> "fi")
    const routeLocale = currentLocale
      ? currentLocale.split('-')[0]
      : this.languageStore.activeLocale;
    const languagePrefix = languages.length > 1 ? `/${routeLocale}` : '';

    let sectionPrefix = '';
    if (activateSections && section) {
      let slug = section.slug;

      // Prefer alternate_links over localized_slugs
      if (section.alternate_links) {
        // Loop through all domains to find a matching locale
        for (const domainKey of Object.keys(section.alternate_links)) {
          if (section.alternate_links[domainKey]) {
            const alternateLink = section.alternate_links[domainKey].find(
              (link) =>
                link.hreflang === currentLocale ||
                link.hreflang.startsWith(currentLocale + '-')
            );
            if (alternateLink) {
              slug = alternateLink.slug;
              // Remove leading slash if present since we'll add it in sectionPrefix
              slug = slug.startsWith('/') ? slug.substring(1) : slug;
              break; // Use first match found
            }
          }
        }
      }

      sectionPrefix = `/${slug}`;
    }

    return languagePrefix + sectionPrefix + path;
  }
}

export default RouteService;
